import { Checkbox, Row, Col } from 'antd';
import { useMemo, useState, useEffect } from 'react';
import cls from 'classnames';
import { intersection } from 'lodash';
import { areaCountryList } from 'src/constants/country';
import { uniqueArray } from 'src/utils';
import styles from './CountryTree.module.scss';

const getDefaultCheckedMap = (checkedCountryIdList: string[]) => {
  return areaCountryList.map((item) => ({
    areaKey: item.areaKey,
    countryCheckedList: intersection(
      checkedCountryIdList,
      item.countryList.map((item) => item.twoLetterCode)
    ),
    countryLen: item.countryList.length,
  }));
};

interface Props {
  value?: string[];
  onChange?(newValue: string[]): void;
  isEn: boolean;
}

const CountryTree: React.FC<Props> = ({ isEn, value, onChange }) => {
  const [areaActiveIdx, setActiveIdx] = useState(0);
  const [areaCheckedMap, setAreaCheckedMap] = useState(
    getDefaultCheckedMap(value || [])
  );

  useEffect(() => {
    setAreaCheckedMap(getDefaultCheckedMap(value || []));
  }, [value]);

  const areaCheckedValue = useMemo(() => {
    return Object.keys(areaCheckedMap)
      .filter((item) => areaCheckedMap[item].countryCheckedList.length !== 0)
      .map((item) => Number(item));
  }, [areaCheckedMap]);

  const handleChangeAreaActiveIdx = (idx: number) => {
    setActiveIdx(idx ?? 1);
  };

  const handleCheckedArea = (areaKey: number, checked: boolean) => {
    if (Array.isArray(areaCheckedMap[areaKey]?.countryCheckedList)) {
      let checkedList: string[] = [];
      if (checked) {
        checkedList = areaCountryList
          .find((item) => item.areaKey === areaKey)!
          .countryList.map((item) => item.twoLetterCode);
      }
      handleChangeCheckedCountryList(checkedList, areaKey);
    }
  };

  const handleChangeCheckedCountryList = (value: string[], areaKey: number) => {
    areaCheckedMap[areaKey].countryCheckedList = value;
    setAreaCheckedMap({ ...areaCheckedMap });
    const checkedCountryCodeList = uniqueArray(
      Object.keys(areaCheckedMap)
        .filter((a) => areaCheckedMap[a].countryCheckedList.length !== 0)
        .reduce(
          (pre, cur) => [...pre, ...areaCheckedMap[cur].countryCheckedList],
          [] as string[]
        )
    );
    onChange && onChange(checkedCountryCodeList);
  };

  const getAreaIsAllCheckedByKey = (areaKey: number) => {
    return (
      areaCheckedMap[areaKey].countryCheckedList.length !==
      areaCheckedMap[areaKey].countryLen
    );
  };

  const renderAreaCheckBox = (item: any) => {
    const isActive = item.areaKey === areaActiveIdx;
    return (
      <div
        key={item.areaName}
        className={cls(styles.checkbox__wrapper, {
          [styles.checkbox__wrapper__active]: isActive,
        })}
        onClick={handleChangeAreaActiveIdx.bind(null, item.areaKey)}
      >
        <Checkbox
          value={item.areaKey}
          onChange={(e) => {
            handleCheckedArea(item.areaKey, e.target.checked);
          }}
          indeterminate={
            areaCheckedValue.includes(item.areaKey) &&
            getAreaIsAllCheckedByKey(item.areaKey)
          }
        />
        <span
          className={cls(styles.area__name, {
            [styles.area__name__active]: isActive,
          })}
        >
          {isEn ? item.areaEnglishName : item.areaName}
        </span>
      </div>
    );
  };

  return (
    <Row className={styles.container}>
      <Col span={isEn ? 12 : 8}>
        <Checkbox.Group className={styles.area} value={areaCheckedValue}>
          {areaCountryList.map(renderAreaCheckBox)}
        </Checkbox.Group>
      </Col>

      <Col span={isEn ? 12 : 16} className={cls(styles.col)}>
        <Checkbox.Group
          className={styles.country}
          value={areaCheckedMap[areaActiveIdx]?.countryCheckedList}
          onChange={(e) => {
            handleChangeCheckedCountryList(
              e as string[],
              areaCountryList[areaActiveIdx].areaKey
            );
          }}
        >
          {areaCountryList[areaActiveIdx].countryList.map((item) => (
            <div key={item.countryId} className={styles.checkbox__wrapper}>
              <Checkbox value={item.twoLetterCode}>
                {isEn ? item.englishName : item.chineseName}
              </Checkbox>
            </div>
          ))}
        </Checkbox.Group>
      </Col>
    </Row>
  );
};

export default CountryTree;
